// SPDX-License-Identifier: AGPL-3.0-or-later

import {getChannelGlyphIcon} from '@app/features/channel/glyph/ChannelGlyphCatalog';
import styles from '@app/features/channel/glyph/ChannelGlyphView.module.css';
import * as EmojiUtils from '@app/features/expressions/utils/EmojiUtils';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import {type ChannelGlyph, parseChannelGlyph} from '@fluxer/constants/src/ChannelGlyph';
import {clsx} from 'clsx';

interface ChannelGlyphViewProps {
	glyph: string | ChannelGlyph | null | undefined;
	className?: string;
	/** Play animated custom emoji (the channel list keeps them still). */
	animate?: boolean;
}

/** Renders a channel glyph (icon, emoji or custom emoji); renders nothing for an unknown value. */
export function ChannelGlyphView({glyph, className, animate = false}: ChannelGlyphViewProps) {
	const parsed = typeof glyph === 'string' || glyph == null ? parseChannelGlyph(glyph) : glyph;
	if (!parsed) return null;
	switch (parsed.kind) {
		case 'icon': {
			const Icon = getChannelGlyphIcon(parsed.key);
			if (!Icon) return null;
			return (
				<span className={clsx(styles.glyph, className)} style={{color: `#${parsed.color}`}} aria-hidden="true">
					<Icon weight={parsed.weight} className={styles.icon} />
				</span>
			);
		}
		case 'emoji': {
			const url = EmojiUtils.getEmojiURL(parsed.emoji);
			return (
				<span className={clsx(styles.glyph, className)} aria-hidden="true">
					{url ? (
						<img className={styles.image} src={url} alt="" draggable={false} />
					) : (
						<span className={styles.text}>{parsed.emoji}</span>
					)}
				</span>
			);
		}
		case 'custom':
			return (
				<span className={clsx(styles.glyph, className)} aria-hidden="true">
					<img
						className={styles.image}
						src={AvatarUtils.getEmojiURL({id: parsed.id, animated: parsed.animated && animate})}
						alt=""
						draggable={false}
						loading="lazy"
					/>
				</span>
			);
	}
}
